import { matchEnum, toNumber } from '../coerce';
import { POINT_VALUE_KEYS } from '../widgets';
import type { LiveRuleIssue, Normalized, WidgetRead } from './entry';

const renamed = (value: Record<string, unknown>): Normalized => ({ value, repairs: [] });

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** The table-column enums the renderer understands (`tableSpec`). */
const COLUMN_ALIGNS = new Set(['left', 'center', 'right']);
const COLUMN_FORMATS = ['number', 'currency', 'percentage', 'text'];

/**
 * Map a column `format` onto the enum, or DROP it.
 *
 * THE FAILURE THIS PREVENTS: `format` is an optional COSMETIC hint, so refusing to draw an entire
 * table because one column said `.1f` is a bad trade — 87 prod payloads died on exactly that
 * (models write printf/Python specs: `.1f`, `0.0%`, `$0.00`, `fixed(2)`, or a style word).
 */
function normalizeColumnFormat(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const matched = matchEnum(value, COLUMN_FORMATS);
  if (matched) return matched;
  const v = value.trim().toLowerCase();
  // Percent before currency before plain number: `.1f%` is a percentage, `$0.00` is money.
  if (v.includes('%')) return 'percentage';
  if (/[$€£]/.test(v)) return 'currency';
  if (/\d/.test(v)) return 'number';
  return undefined;
}

/** An ISO 4217 code as a model writes it in a `format` (`'PLN'`): three capitals, nothing else. */
const ISO_CODE = /^[A-Z]{3}$/;

/**
 * A column's `format` and `currency` as ONE valid pair, and whether a money claim was dropped.
 *
 * THE FAILURE THIS PREVENTS (test audit C-80): a `currency` column carried no code and the
 * renderer drew it in USD, so a PLN book read "$189,685.89". A code the model wrote is kept, in
 * `currency` or AS the format (`'PLN'`: 32 prod columns); a money column that names none — every
 * table stored before the rule (2,370 prod columns) — is drawn as a plain number, never refused:
 * no symbol is incomplete, the wrong one is false.
 */
function columnFormat(col: Record<string, unknown>): {
  format?: string;
  currency?: string;
  droppedMoney: boolean;
} {
  const asCode = typeof col.format === 'string' && ISO_CODE.test(col.format.trim());
  const format = normalizeColumnFormat(col.format) ?? (asCode ? 'currency' : undefined);
  if (format !== 'currency') return { format, droppedMoney: false };
  const sent = typeof col.currency === 'string' ? col.currency.trim().toUpperCase() : '';
  const formatCode = asCode ? String(col.format).trim() : undefined;
  const currency = ISO_CODE.test(sent) ? sent : formatCode;
  return currency
    ? { format, currency, droppedMoney: false }
    : { format: 'number', droppedMoney: true };
}

const PERCENT_UNITS = ['points', 'fraction'];

/**
 * A one-day move or a fee: labels whose percent POINTS run under 1 %, so a column of values at or
 * under 1 says nothing about its unit (`dziś` 0.094, `1D` 0.28, `TER` 0.098 in the prod census).
 */
// ponytail: a label list for STORED columns only; a points column of sub-1 values under a label it
// misses is drawn times 100. A live call must state `percentUnit`, so the list never has to grow.
// A yield is here too: 0.44 is a 0.44 % yield far more often than a 44 % one (review R4a).
// Two patterns, the one-day moves and the fees and yields, each under Sonar's regex-complexity bar.
const SUB_ONE_POINTS_LABELS = [
  /\b(1d|day|daily|today|session|intraday)\b|dzi[sś]|dzien|sesj|heute|hoy|сегодн/i,
  /\b(ter|fees?)\b|opłat|koszt|gebühr|comisi|yield|stopa dyw|rendite|доходн/i,
];

/** A cell as a number: a number, or a string the healer reads as one ("12,5"). */
function cellNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  return typeof v === 'string' ? toNumber(v) : undefined;
}

/** The column's numeric cells: numbers, or strings the healer reads as one ("12,5"). */
function columnValues(rows: unknown[], key: unknown): number[] {
  return rows.flatMap((row) => {
    const n = cellNumber(isPlainObject(row) ? row[String(key)] : undefined);
    return n !== undefined && Number.isFinite(n) ? [n] : [];
  });
}

/**
 * A `percentage` column's unit: the one it states, else the one its own values show.
 *
 * THE FAILURE THIS PREVENTS (test audit C-207): the unit was never stated and the model sent
 * both. Prod census 2026-09-30: 579 percentage columns (164 threads) hold only values at or under
 * 1 — margins, weights, growth rates, drawn as "0.74%" — beside 1,494 in points. So a stored
 * column is read by its values: all at or under 1 (and not all 0) is a FRACTION, unless its label
 * names a one-day move, a fee or a yield; anything else is POINTS, what the renderer always assumed.
 * `undefined` while `rows` is not an array yet — the healer parses it, and this runs again.
 *
 * STORED ONLY (review R4a of #3229): on a LIVE read the unit is left absent, so the schema refuses
 * the column and the model states it. Inferred live, a points yield of [0.44, 0.72] rendered 44 %
 * and the model was never told.
 */
function columnPercentUnit(
  col: Record<string, unknown>,
  rows: unknown,
  read: WidgetRead,
): { percentUnit?: string; inferred: boolean } {
  const sent =
    typeof col.percentUnit === 'string' ? matchEnum(col.percentUnit, PERCENT_UNITS) : undefined;
  if (sent) return { percentUnit: sent, inferred: false };
  if (read === 'live' || !Array.isArray(rows)) return { inferred: false };
  const values = columnValues(rows, col.key ?? col.id);
  // A label that is not a string names no unit (and the schema refuses the column for it).
  const caption = col.label ?? col.header;
  const label = typeof caption === 'string' ? caption : '';
  const fraction =
    values.some((v) => v !== 0) &&
    values.every((v) => Math.abs(v) <= 1) &&
    !SUB_ONE_POINTS_LABELS.some((pattern) => pattern.test(label));
  return { percentUnit: fraction ? 'fraction' : 'points', inferred: true };
}

/**
 * THE FAILURE THIS PREVENTS: models write table columns as `{ id, header }` (the TanStack/AG-Grid
 * shape) or omit `columns` entirely and send only `rows` — both render nothing without this.
 */
export function normalizeTable(raw: Record<string, unknown>, read: WidgetRead): Normalized {
  const out = { ...raw };
  if (Array.isArray(out.columns)) {
    const repairs: Normalized['repairs'] = [];
    out.columns = out.columns.map((col, i) => {
      if (!isPlainObject(col)) return col;
      const { format, currency, droppedMoney } = columnFormat(col);
      if (droppedMoney) repairs.push(`coerced:props.columns.${i}.format`);
      const unit = format === 'percentage' ? columnPercentUnit(col, out.rows, read) : undefined;
      if (unit?.inferred) repairs.push(`coerced:props.columns.${i}.percentUnit`);
      return {
        ...col,
        key: col.key ?? col.id,
        label: col.label ?? col.header,
        align:
          typeof col.align === 'string' && COLUMN_ALIGNS.has(col.align) ? col.align : undefined,
        format,
        ...(currency ? { currency } : {}),
        ...(unit?.percentUnit ? { percentUnit: unit.percentUnit } : {}),
      };
    });
    return { value: out, repairs };
  }
  // Columns sent as a JSON string are the model's own labels: the healer parses them, and this
  // runs again on the parsed array. Deriving here would overwrite them with bare row keys.
  if (typeof out.columns === 'string') return renamed(out);
  // No columns at all: derive them from the first row, which is the only description of the
  // table the model gave us. A key IS a readable label far more often than it is not.
  const firstRow = Array.isArray(out.rows) ? out.rows[0] : undefined;
  if (isPlainObject(firstRow)) {
    out.columns = Object.keys(firstRow).map((key) => ({ key, label: key }));
  }
  return renamed(out);
}

/**
 * THE FAILURE THIS PREVENTS: the model names the KPI array `rows`/`items`/`cards` (the words the
 * data it just read used), and a `benchmark` is sent as a bare `{ label }` with no figure — both
 * rejected the whole group in prod.
 */
export function normalizeStatGroup(raw: Record<string, unknown>): Normalized {
  const out = { ...raw };
  const stats = out.stats ?? out.rows ?? out.items ?? out.cards;
  delete out.rows;
  delete out.items;
  delete out.cards;
  if (stats === undefined) return renamed(out);
  if (!Array.isArray(stats)) return renamed({ ...out, stats });
  out.stats = stats.map((stat) => {
    if (!isPlainObject(stat)) return stat;
    const benchmark = stat.benchmark;
    if (isPlainObject(benchmark) && typeof benchmark.value !== 'string') {
      const withoutBenchmark = { ...stat };
      delete withoutBenchmark.benchmark;
      return withoutBenchmark;
    }
    return stat;
  });
  return renamed(out);
}

/**
 * THE FAILURE THIS PREVENTS: models send allocation weights as PERCENTS (35, 65) although the
 * schema says fractions, and every slice then clamps to 100% — an all-full bar that looks broken.
 * Only rewritten when EVERY weight is > 1 and ≤ 100, so a real fraction set is never touched.
 */
export function normalizeAllocation(raw: Record<string, unknown>): Normalized {
  const items = raw.items;
  if (!Array.isArray(items) || items.length === 0) return renamed(raw);
  const weights = items.map((i) => (isPlainObject(i) ? i.weight : undefined));
  const allPercent = weights.every((w) => typeof w === 'number' && w > 1 && w <= 100);
  if (!allPercent) return renamed(raw);
  return renamed({
    ...raw,
    items: items.map((item, index) =>
      isPlainObject(item) ? { ...item, weight: (weights[index] as number) / 100 } : item,
    ),
  });
}

/** Keys a chart row names its point by (the renderer's label set, `chart-option.ts`). */
const CHART_LABEL_KEYS = new Set(['name', 'label', 'category', 'x', 'date', 'symbol', 'key']);
const POINT_KEYS = new Set<string>(POINT_VALUE_KEYS);

/** A finite number, or a numeral `toNumber` reads without guessing; else `undefined`. */
function seriesNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  return typeof v === 'string' ? toNumber(v) : undefined;
}

/** The one string label key of a row, or `undefined` when there is none or more than one. */
function rowLabelKey(row: Record<string, unknown>): string | undefined {
  const keys = Object.keys(row).filter(
    (k) => CHART_LABEL_KEYS.has(k) && typeof row[k] === 'string',
  );
  return keys.length === 1 ? keys[0] : undefined;
}

/** Every row carries exactly `keys` (any order). */
function sameKeys(rows: Record<string, unknown>[], keys: string[]): boolean {
  return rows.every((r) => Object.keys(r).length === keys.length && keys.every((k) => k in r));
}

/**
 * Wide rows (`[{ name, USD, EUR }]`) as the Chart.js `{ labels, datasets }` shape `ChartData`
 * already accepts, or `undefined` when the rows are not UNAMBIGUOUSLY that.
 *
 * THE FAILURE THIS PREVENTS: 15 prod multi-series charts refused in 30 days —
 * `[{"name":"2021*","USD":15.31,"EUR2":4.68}]`, `[{"name":"Start","ScenariuszBase":30968,…}]`.
 * No row carries a `value`, so nothing is plottable and the model retried with ONE series,
 * dropping the comparison the user asked for. Reshaped only when every row has the same keys,
 * exactly one string label key, and ≥2 series whose every cell reads as a number; a row that is
 * already a point (`value`/`y`/…) is left alone, since the renderer draws it as it stands.
 */
function wideRowsToDatasets(data: unknown): Record<string, unknown> | undefined {
  if (!Array.isArray(data) || data.length === 0 || !data.every(isPlainObject)) return undefined;
  const rows: Record<string, unknown>[] = data;
  const labelKey = rowLabelKey(rows[0]);
  if (!labelKey) return undefined;
  const seriesKeys = Object.keys(rows[0]).filter((k) => k !== labelKey);
  if (seriesKeys.length < 2 || seriesKeys.some((k) => POINT_KEYS.has(k))) return undefined;
  if (!sameKeys(rows, Object.keys(rows[0])) || !rows.every((r) => rowLabelKey(r) === labelKey)) {
    return undefined;
  }
  const datasets = seriesKeys.map((k) => ({
    label: k,
    data: rows.map((r) => seriesNumber(r[k])),
  }));
  if (datasets.some((d) => d.data.includes(undefined))) return undefined;
  return { labels: rows.map((r) => String(r[labelKey])), datasets };
}

export function normalizeChart(raw: Record<string, unknown>): Normalized {
  const reshaped = wideRowsToDatasets(raw.data);
  if (!reshaped) return renamed(raw);
  return { value: { ...raw, data: reshaped }, repairs: ['reshaped:props.data'] };
}

/** A percentage figure that does not state its unit: a live writer is told to. */
function missingPercentUnit(stat: unknown, path: PropertyKey[]): LiveRuleIssue[] {
  if (!isPlainObject(stat) || stat.format !== 'percentage' || stat.percentUnit !== undefined) {
    return [];
  }
  return [
    {
      path: [...path, 'percentUnit'],
      message:
        "A percentage value states percentUnit: 'points' (12.4 means 12.4%) or 'fraction' (0.124 means 12.4%).",
    },
  ];
}

/** Live rule (review R5c): a percentage stat tile states its unit, as a table column must. */
export function statCardLiveRules(props: unknown): LiveRuleIssue[] {
  return missingPercentUnit(props, []);
}

/** Live rule (review R5c): every percentage entry of a stat_group states its unit. */
export function statGroupLiveRules(props: unknown): LiveRuleIssue[] {
  if (!isPlainObject(props) || !Array.isArray(props.stats)) return [];
  return props.stats.flatMap((stat, i) => missingPercentUnit(stat, ['stats', i]));
}
